<div class="variants-header">
    <div>
        <div class="variants-title">Variants</div>
        <div class="variants-subtitle">Add options like size or color</div>
    </div>
    <button
        *ngIf="options.length && !isViewOnly"
        type="button"
        class="variants-add-inline"
        (click)="openAddOptionPanel($event)"
    >
        <i class="pi pi-plus"></i>
        Add option
    </button>
</div>

<p-overlayPanel
    #addOptionPanel
    styleClass="variants-add-overlay"
    (onShow)="onAddOptionPanelShow()"
    (onHide)="onAddOptionPanelHide()"
>
    <div class="variants-add-panel">
        <div class="variants-add-search">
            <i class="pi pi-search"></i>
            <input
                #addOptionSearchInput
                type="text"
                class="variants-add-input"
                placeholder="Search"
                autocomplete="off"
                maxlength="80"
                [(ngModel)]="addOptionSearch"
                (ngModelChange)="onAddOptionSearchChange($event)"
                (keydown)="onAddOptionSearchKeydown($event)"
            />
        </div>
        <div class="variants-add-section">Recommended</div>
        <div class="variants-add-loading" *ngIf="isPresetLoading" aria-live="polite">
            Loading recommended options...
        </div>
        <div class="variants-add-error" *ngIf="!isPresetLoading && presetLoadError" aria-live="polite">
            {{ presetLoadError }}
        </div>
        <div class="variants-add-list">
            <button
                type="button"
                class="variants-add-item"
                *ngFor="let suggestion of filteredAddOptionSuggestions; let si = index"
                [class.active]="si === highlightedAddOptionIndex"
                [attr.aria-current]="si === highlightedAddOptionIndex ? 'true' : null"
                (mouseenter)="highlightedAddOptionIndex = si"
                (touchstart)="onSuggestedOptionTouchStart($event, suggestion)"
                (click)="selectSuggestedOption(suggestion)"
            >
                {{ suggestion }}
            </button>
            <div
                class="variants-add-empty"
                *ngIf="!isPresetLoading && !presetLoadError && !filteredAddOptionSuggestions.length && addOptionSearch?.trim()"
                aria-live="polite"
            >
                No matching options found.
            </div>
        </div>
        <button
            type="button"
            class="variants-add-custom"
            (touchstart)="onCreateCustomOptionTouchStart($event)"
            (click)="createCustomOption()"
            [disabled]="isViewOnly"
        >
            <i class="pi pi-plus"></i>
            Create custom option
        </button>
        <div class="variants-add-error" *ngIf="customOptionError" aria-live="polite">
            {{ customOptionError }}
        </div>
    </div>
</p-overlayPanel>

<div class="variants-empty" *ngIf="!options.length">
    <button
        type="button"
        class="variants-add-primary"
        (click)="openAddOptionPanel($event)"
        [disabled]="isViewOnly"
    >
        <i class="pi pi-plus-circle"></i>
        Add options like size or color
    </button>
    <div class="variants-empty-note" *ngIf="isViewOnly">
        No variants defined for this product.
    </div>
</div>

<div
    class="variants-list"
    *ngIf="options.length"
    cdkDropList
    [cdkDropListDisabled]="isViewOnly"
    [cdkDropListData]="options"
    (cdkDropListDropped)="onOptionDrop($event)"
>
    <div
        class="variant-option"
        *ngFor="let option of options; let i = index; trackBy: trackByIndex"
        cdkDrag
        [cdkDragDisabled]="isViewOnly"
    >
        <div
            class="variant-summary-row"
            *ngIf="!isEditingOption(i) && option.values?.length"
        >
            <div class="variant-grip" cdkDragHandle>
                <i class="pi pi-bars"></i>
            </div>
            <div class="variant-summary">
                <div class="variant-summary-name">
                    {{ option.name || 'Option name' }}
                </div>
                <div class="variant-summary-chips">
                    <span class="variant-chip" *ngFor="let value of option.values">
                        {{ value }}
                    </span>
                </div>
            </div>
            <button
                *ngIf="!isViewOnly"
                type="button"
                class="variant-summary-icon"
                (click)="startEditOption(i)"
            >
                <i class="pi pi-chevron-down"></i>
            </button>
        </div>
        <div
            class="variant-option-row"
            *ngIf="isEditingOption(i) || !option.values?.length"
        >
            <div class="variant-grip" cdkDragHandle>
                <i class="pi pi-bars"></i>
            </div>
            <div class="variant-fields">
                <label class="control-label">
                    <strong>Option name</strong>
                </label>
                <ng-container *ngIf="!option.isCustomName || isFixedVariantName(option.name); else customOptionName">
                    <p-autoComplete
                        [(ngModel)]="option.name"
                        [suggestions]="filteredOptionSuggestions"
                        (completeMethod)="filterOptionSuggestions($event, i)"
                        [dropdown]="true"
                        [forceSelection]="false"
                        [disabled]="isViewOnly"
                        placeholder="Color"
                        (ngModelChange)="onOptionNameChange(i)"
                        (onBlur)="onOptionNameBlur(i)"
                        [ngClass]="{ 'is-invalid': optionNameErrors.has(i) }"
                        panelStyleClass="variants-option-name-panel"
                        appendTo="body"
                    >
                        <ng-template pTemplate="header">
                            <div class="variants-panel-header">Recommended</div>
                        </ng-template>
                    </p-autoComplete>
                </ng-container>
                <ng-template #customOptionName>
                    <input
                        type="text"
                        class="p-inputtext variant-option-input"
                        [(ngModel)]="option.name"
                        [disabled]="isViewOnly"
                        placeholder="Custom option"
                        (ngModelChange)="onOptionNameChange(i)"
                        (blur)="onOptionNameBlur(i)"
                        [ngClass]="{ 'is-invalid': optionNameErrors.has(i) }"
                    />
                </ng-template>
                <div class="variant-error" *ngIf="optionNameErrors.has(i)">
                    Option name must be unique.
                </div>

                <label class="control-label">
                    <strong>Option values</strong>
                </label>
                <ng-container *ngIf="getFixedValueOptions(option).length">
                    <p-multiSelect
                        #optionMultiSelect
                        [(ngModel)]="option.values"
                        [options]="getFixedValueOptions(option)"
                        optionLabel="label"
                        optionValue="value"
                        [filter]="true"
                        [showToggleAll]="false"
                        [disabled]="isViewOnly"
                        [closeOnSelect]="false"
                        defaultLabel="Select value(s)"
                        (onChange)="onOptionValuesChange(i, $event.value, optionMultiSelect)"
                        panelStyleClass="variants-value-panel"
                        appendTo="body"
                    ></p-multiSelect>
                </ng-container>

                <ng-container *ngIf="isColorOption(option)">
                    <p-multiSelect
                        #optionMultiSelect
                        [(ngModel)]="option.values"
                        [options]="option.cachedColorOptions"
                        optionLabel="name"
                        optionValue="name"
                        [filter]="true"
                        [showToggleAll]="false"
                        [disabled]="isViewOnly"
                        [closeOnSelect]="false"
                        defaultLabel="Select color(s)"
                        (onChange)="onOptionValuesChange(i, $event.value, optionMultiSelect)"
                        panelStyleClass="variants-color-panel"
                        appendTo="body"
                    >
                        <ng-template pTemplate="selectedItems" let-selectedValues>
                            {{ formatSelectedItemsSummary(selectedValues, 'Select color(s)') }}
                        </ng-template>
                        <ng-template pTemplate="header">
                            <div class="variants-panel-header">
                                Default entries
                            </div>
                        </ng-template>
                        <ng-template pTemplate="item" let-item>
                            <div class="color-option">
                                <span
                                    class="color-swatch"
                                    [style.background]="item?.hex || '#ffffff'"
                                ></span>
                                <span>{{ item.name }}</span>
                            </div>
                        </ng-template>
                    </p-multiSelect>
                    <span class="control-description text-muted">
                        Choose from default color entries or add a custom value.
                    </span>
                </ng-container>

                <ng-container *ngIf="!isColorOption(option) && isSizeOption(option)">
                    <p-multiSelect
                        #optionMultiSelect
                        [(ngModel)]="option.values"
                        [options]="getSizeOptions(option)"
                        optionLabel="label"
                        optionValue="value"
                        [filter]="true"
                        [showToggleAll]="false"
                        [disabled]="isViewOnly"
                        [closeOnSelect]="false"
                        defaultLabel="Select size(s)"
                        filterPlaceholder="Search sizes"
                        (onChange)="onOptionValuesChange(i, $event.value, optionMultiSelect)"
                        panelStyleClass="variants-size-panel"
                        appendTo="body"
                    >
                        <ng-template pTemplate="selectedItems" let-selectedValues>
                            {{ formatSelectedItemsSummary(selectedValues, 'Select size(s)') }}
                        </ng-template>
                        <ng-template pTemplate="header">
                            <div class="variants-panel-header">
                                Default entries
                            </div>
                        </ng-template>
                        <ng-template pTemplate="item" let-item>
                            <div class="flex align-items-center gap-2">
                                <span>{{ item.label }}</span>
                            </div>
                        </ng-template>
                    </p-multiSelect>
                    <span class="control-description text-muted">
                        Choose from default size entries or add a custom value.
                    </span>
                </ng-container>

                <ng-container *ngIf="!isColorOption(option) && isMaterialOption(option)">
                    <p-multiSelect
                        #optionMultiSelect
                        [(ngModel)]="option.values"
                        [options]="getMaterialOptions(option)"
                        optionLabel="label"
                        optionValue="value"
                        [filter]="true"
                        [showToggleAll]="false"
                        [disabled]="isViewOnly"
                        [closeOnSelect]="false"
                        defaultLabel="Select material(s)"
                        filterPlaceholder="Search materials"
                        (onChange)="onOptionValuesChange(i, $event.value, optionMultiSelect)"
                        panelStyleClass="variants-material-panel"
                        appendTo="body"
                    >
                        <ng-template pTemplate="selectedItems" let-selectedValues>
                            {{ formatSelectedItemsSummary(selectedValues, 'Select material(s)') }}
                        </ng-template>
                        <ng-template pTemplate="header">
                            <div class="variants-panel-header">
                                Default entries
                            </div>
                        </ng-template>
                        <ng-template pTemplate="item" let-item>
                            <div class="flex align-items-center gap-2">
                                <span>{{ item.label }}</span>
                            </div>
                        </ng-template>
                    </p-multiSelect>
                    <span class="control-description text-muted">
                        Choose from default material entries or add a custom value.
                    </span>
                </ng-container>

                <ng-container *ngIf="!isColorOption(option) && isStyleOption(option)">
                    <p-multiSelect
                        #optionMultiSelect
                        [(ngModel)]="option.values"
                        [options]="option.cachedStyleOptions"
                        optionLabel="label"
                        optionValue="value"
                        [filter]="true"
                        [showToggleAll]="false"
                        [disabled]="isViewOnly"
                        [closeOnSelect]="false"
                        defaultLabel="Select style(s)"
                        filterPlaceholder="Search styles"
                        (onChange)="onOptionValuesChange(i, $event.value, optionMultiSelect)"
                        panelStyleClass="variants-style-panel"
                        appendTo="body"
                    >
                        <ng-template pTemplate="selectedItems" let-selectedValues>
                            {{ formatSelectedItemsSummary(selectedValues, 'Select style(s)') }}
                        </ng-template>
                        <ng-template pTemplate="header">
                            <div class="variants-panel-header">
                                Default entries
                            </div>
                        </ng-template>
                    </p-multiSelect>
                    <span class="control-description text-muted">
                        Choose from default style entries or add a custom value.
                    </span>
                </ng-container>

                <ng-container *ngIf="!isColorOption(option) && isTargetGenderOption(option)">
                    <p-multiSelect
                        #optionMultiSelect
                        [(ngModel)]="option.values"
                        [options]="getGenderOptions(option)"
                        optionLabel="label"
                        optionValue="value"
                        [filter]="true"
                        [showToggleAll]="false"
                        [disabled]="isViewOnly"
                        [closeOnSelect]="false"
                        defaultLabel="Select gender(s)"
                        filterPlaceholder="Search genders"
                        (onChange)="onOptionValuesChange(i, $event.value, optionMultiSelect)"
                        panelStyleClass="variants-gender-panel"
                        appendTo="body"
                    >
                        <ng-template pTemplate="selectedItems" let-selectedValues>
                            {{ formatSelectedItemsSummary(selectedValues, 'Select gender(s)') }}
                        </ng-template>
                        <ng-template pTemplate="header">
                            <div class="variants-panel-header">
                                Default entries
                            </div>
                        </ng-template>
                    </p-multiSelect>
                    <span class="control-description text-muted">
                        Choose from default gender entries or add a custom value.
                    </span>
                </ng-container>

                <ng-container *ngIf="!isColorOption(option) && isLengthOption(option)">
                    <p-multiSelect
                        #optionMultiSelect
                        [(ngModel)]="option.values"
                        [options]="getLengthOptions(option)"
                        optionLabel="label"
                        optionValue="value"
                        [filter]="true"
                        [showToggleAll]="false"
                        [disabled]="isViewOnly"
                        [closeOnSelect]="false"
                        defaultLabel="Select length(s)"
                        filterPlaceholder="Search lengths"
                        (onChange)="onOptionValuesChange(i, $event.value, optionMultiSelect)"
                        panelStyleClass="variants-length-panel"
                        appendTo="body"
                    >
                        <ng-template pTemplate="selectedItems" let-selectedValues>
                            {{ formatSelectedItemsSummary(selectedValues, 'Select length(s)') }}
                        </ng-template>
                        <ng-template pTemplate="header">
                            <div class="variants-panel-header">
                                Default entries
                            </div>
                        </ng-template>
                    </p-multiSelect>
                    <span class="control-description text-muted">
                        Choose from default length entries or add a custom value.
                    </span>
                </ng-container>

                <div
                    class="variant-values"
                    cdkDropList
                    [cdkDropListData]="option.values"
                    (cdkDropListDropped)="onValueDrop($event, option)"
                >
                    <div
                        class="variant-value"
                        [class.has-color-picker]="isColorOption(option)"
                        *ngFor="let value of option.values; let vi = index; trackBy: trackByValueIndex"
                        cdkDrag
                    >
                        <div class="value-grip" cdkDragHandle>
                            <i class="pi pi-bars"></i>
                        </div>
                        <ng-container *ngIf="getValueLeadingSwatch(option, value) as swatch; else valueSwatchPlaceholder">
                            <span
                                class="value-swatch"
                                [style.background]="swatch"
                            ></span>
                        </ng-container>
                        <ng-template #valueSwatchPlaceholder>
                            <span class="value-swatch placeholder"></span>
                        </ng-template>
                        <input
                            type="text"
                            class="p-inputtext value-input"
                            [(ngModel)]="option.values[vi]"
                            [disabled]="isViewOnly"
                            (focus)="onValueEditStart(option, vi)"
                            (blur)="onValueEditCommit(i, option, vi, $event)"
                            (keydown.enter)="$event.preventDefault(); onValueEditCommit(i, option, vi, $event)"
                            (keydown.tab)="onValueInputTab($event)"
                        />
                        <button
                            *ngIf="isColorOption(option)"
                            type="button"
                            class="color-picker-button"
                            (mousedown)="prepareExistingColorPick()"
                            [disabled]="isViewOnly"
                            [title]="'Pick a color'"
                        >
                            <span
                                class="color-picker-preview"
                                [style.background]="getEditableColorValue(option, option.values[vi])"
                            ></span>
                            <input
                                type="color"
                                class="color-picker-input color-picker-native"
                                [ngModel]="getEditableColorValue(option, option.values[vi])"
                                (ngModelChange)="onExistingColorPick(option, option.values[vi], $event)"
                                [disabled]="isViewOnly"
                                tabindex="-1"
                                (mousedown)="prepareExistingColorPick(); $event.stopPropagation()"
                                (click)="$event.stopPropagation()"
                            />
                        </button>
                        <button
                            *ngIf="!isViewOnly"
                            type="button"
                            class="value-action"
                            (click)="removeValue(option, vi)"
                            [disabled]="isViewOnly"
                        >
                            <i class="pi pi-trash"></i>
                        </button>
                    </div>
                </div>

                <div class="variant-add-row">
                    <div class="value-grip placeholder">
                        <i class="pi pi-bars"></i>
                    </div>
                    <span
                        class="value-swatch"
                        [class.placeholder]="!isColorOption(option)"
                        [style.background]="isColorOption(option) ? (option.colorDraft || '#ffffff') : null"
                    ></span>
                        <input
                            type="text"
                            class="p-inputtext value-input"
                            [(ngModel)]="option.draftValue"
                            (ngModelChange)="onDraftValueChange(option, $event)"
                            [disabled]="isViewOnly"
                            [placeholder]="getAddValuePlaceholder(option)"
                            (keydown.enter)="$event.preventDefault(); addValue(option)"
                            (keydown.tab)="onValueInputTab($event)"
                            (blur)="onDraftValueBlur(option, $event)"
                        />
                    <div class="value-actions" *ngIf="!isViewOnly">
                        <button
                            *ngIf="isColorOption(option)"
                            type="button"
                            class="color-picker-button"
                            (mousedown)="prepareDraftColorPick()"
                            [disabled]="isViewOnly"
                            [title]="'Pick a color'"
                        >
                            <span
                                class="color-picker-preview"
                                [style.background]="option.colorDraft || '#000000'"
                            ></span>
                            <input
                                type="color"
                                class="color-picker-input color-picker-native"
                                [(ngModel)]="option.colorDraft"
                                (input)="onCustomColorPick(option, option.colorDraft || $event.target?.value)"
                                [disabled]="isViewOnly"
                                tabindex="-1"
                                (mousedown)="prepareDraftColorPick(); $event.stopPropagation()"
                                (click)="$event.stopPropagation()"
                            />
                        </button>
                        <button
                            type="button"
                            class="value-action add"
                            (click)="addValue(option)"
                            [disabled]="isViewOnly"
                        >
                            <i class="pi pi-plus"></i>
                        </button>
                    </div>
                </div>

                <div class="variant-error" *ngIf="optionValueErrors.has(i)">
                    {{ optionValueErrors.get(i) }}
                </div>

                <div class="variant-actions" *ngIf="!isViewOnly">
                    <button
                        type="button"
                        class="variants-link delete"
                        (click)="removeOption(i)"
                    >
                        Delete
                    </button>
                    <button
                        type="button"
                        class="variants-link done"
                        (click)="finishEditOption(i)"
                        [disabled]="optionNameErrors.has(i)"
                    >
                        Done
                    </button>
                </div>
            </div>
        </div>
    </div>

    <button
        *ngIf="!isViewOnly"
        type="button"
        class="variants-add-secondary"
        (click)="openAddOptionPanel($event)"
        [disabled]="isViewOnly"
    >
        <i class="pi pi-plus-circle"></i>
        Add another option
    </button>
</div>

<div
    class="variants-table"
    *ngIf="variantRows.length"
    [style.--variants-grid-columns]="variantsGridColumns"
>
    <div class="variants-table-header">
        <div class="group-by">
            <span>Group by</span>
            <p-dropdown
                [options]="groupByOptionsList"
                [(ngModel)]="groupByOption"
                [disabled]="isViewOnly || groupByOptionsList.length < 1"
                (onChange)="onGroupByChange()"
                appendTo="body"
            ></p-dropdown>
        </div>
        <div class="group-by">
            <span>Weight Unit</span>
            <p-dropdown
                [options]="_abd.WeightUOM"
                [ngModel]="_productService.selectedShippingWeight"
                (ngModelChange)="onVariantWeightUomChange($event)"
                optionLabel="title"
                [filter]="false"
                [disabled]="isViewOnly"
                appendTo="body"
            ></p-dropdown>
        </div>
        <div class="table-actions">
            <div class="variant-search" *ngIf="showSearch">
                <i class="pi pi-search"></i>
                <input
                    type="text"
                    class="variant-search-input"
                    placeholder="Search variants"
                    [(ngModel)]="searchQuery"
                    (ngModelChange)="onSearchChange($event)"
                />
                <button
                    type="button"
                    class="icon-button ghost"
                    (click)="toggleSearch()"
                >
                    <i class="pi pi-times"></i>
                </button>
            </div>
            <button
                type="button"
                class="icon-button"
                *ngIf="!showSearch"
                (click)="toggleSearch()"
            >
                <i class="pi pi-search"></i>
            </button>
            <button
                type="button"
                class="icon-button"
                (click)="variantMenu.toggle($event)"
            >
                <i class="pi pi-ellipsis-v"></i>
            </button>
            <p-menu
                #variantMenu
                [popup]="true"
                [model]="variantActionMenu"
                appendTo="body"
                styleClass="variants-action-menu"
            ></p-menu>
        </div>
    </div>

    <div class="variants-bulk" *ngIf="bulkEditMode && !isViewOnly">
        <!-- <div class="bulk-title">
            {{
                bulkEditMode === 'price'
                    ? 'Edit prices'
                    : bulkEditMode === 'quantity'
                    ? 'Edit quantities'
                    : bulkEditMode === 'sku'
                    ? 'Edit SKUs'
                    : 'Edit barcodes'
            }}
        </div> -->
        <div class="bulk-input">
            <ng-container [ngSwitch]="bulkEditMode">
                <div *ngSwitchCase="'price'" class="price-wrapper variant-price">
                    <span class="price-prefix">{{ "" | currency | currencySymbol }}</span>
                    <p-inputNumber
                        [style]="{ width: '100%' }"
                        inputStyleClass="table-input variant-price-input"
                        [(ngModel)]="bulkEditValue"
                        [disabled]="isViewOnly"
                        mode="decimal"
                        [format]="true"
                        [useGrouping]="true"
                        [min]="0"
                        [max]="100000"
                        [minFractionDigits]="2"
                        [maxFractionDigits]="2"
                        placeholder="0.00"
                    ></p-inputNumber>
                </div>
                <input
                    *ngSwitchCase="'quantity'"
                    class="table-input"
                    type="number"
                    min="0"
                    step="1"
                    [ngModel]="bulkEditValue"
                    (ngModelChange)="onBulkQuantityChange($event)"
                    (keydown)="onQuantityKeydown($event)"
                    [disabled]="isViewOnly"
                />
                <input
                    *ngSwitchCase="'sku'"
                    class="table-input"
                    type="text"
                    [(ngModel)]="bulkEditValue"
                    [disabled]="isViewOnly"
                />
                <input
                    *ngSwitchCase="'barcode'"
                    class="table-input"
                    type="text"
                    [(ngModel)]="bulkEditValue"
                    [disabled]="isViewOnly"
                />
            </ng-container>
            <span class="bulk-note" *ngIf="!selectedVariantIds.size">
                Select variants to apply.
            </span>
        </div>
        <div class="bulk-actions">
            <button
                type="button"
                class="icon-button ghost"
                (click)="clearBulkEdit()"
            >
                Cancel
            </button>
            <button
                type="button"
                class="icon-button primary"
                (click)="applyBulkEdit()"
                [disabled]="isViewOnly || !selectedVariantIds.size"
            >
                Apply
            </button>
        </div>
    </div>

    <div class="variants-no-results" *ngIf="!filteredVariantRows.length">
        No variants found<span *ngIf="searchQuery?.trim()"> for "{{ searchQuery.trim() }}"</span>.
    </div>

    <div class="variants-table-scroll" *ngIf="filteredVariantRows.length" [class.wide]="hasWideColumns">
        <div class="variants-grid-header">
        <div class="cell checkbox" *ngIf="showSelectionColumn">
            <input
                type="checkbox"
                [checked]="areAllVisibleSelected()"
                (change)="onSelectAllChange($event)"
                [disabled]="isViewOnly || !filteredVariantRows.length"
            />
        </div>
        <div class="cell variant-title">
            Variant
            <button
                type="button"
                class="collapse-link"
                *ngIf="groupedVariantsList.length > 1"
                (click)="toggleAllGroups()"
            >
                Collapse all
            </button>
        </div>
        <div class="cell image" *ngIf="showImageColumn">Image</div>
        <div class="cell price" *ngIf="showPriceColumn">Price</div>
        <div class="cell sku" *ngIf="showSkuColumn">SKU</div>
        <div class="cell barcode" *ngIf="showBarcodeColumn">Barcode</div>
        <div class="cell available" *ngIf="showQuantityColumn">Quantity</div>
        <div class="cell shipping" *ngIf="showShippingColumn">Shipping</div>
        <div class="cell actions" *ngIf="showRowActionsColumn"></div>
        </div>

        <ng-container *ngFor="let group of groupedVariantsList">
        <div class="variants-group-row" *ngIf="groupedVariantsList.length > 1">
            <div class="cell checkbox" *ngIf="showSelectionColumn">
                <input type="checkbox" disabled />
            </div>
            <div class="cell variant-title">
                <div class="variant-label">
                    <div class="variant-text">
                        <div class="variant-name">{{ group.label }}</div>
                        <div class="variant-sub">
                            {{ group.rows.length }} variants
                        </div>
                    </div>
                    <button
                        type="button"
                        class="icon-button small"
                        (click)="toggleGroup(group.key)"
                    >
                        <i
                            class="pi"
                            [ngClass]="
                                isGroupCollapsed(group.key)
                                    ? 'pi-chevron-down'
                                    : 'pi-chevron-up'
                            "
                        ></i>
                    </button>
                </div>
            </div>
            <div class="cell image" *ngIf="showImageColumn">
                <div class="variant-image-cell readonly">-</div>
            </div>
            <div class="cell price" *ngIf="showPriceColumn">
                <div class="price-wrapper variant-price readonly">
                    <span class="price-prefix">{{ "" | currency | currencySymbol }}</span>
                    <input
                        class="table-input variant-price-input"
                        [value]="getGroupPriceDisplay(group)"
                        disabled
                    />
                </div>
            </div>
            <div class="cell sku" *ngIf="showSkuColumn">
                <input class="table-input" value="-" disabled />
            </div>
            <div class="cell barcode" *ngIf="showBarcodeColumn">
                <input class="table-input" value="-" disabled />
            </div>
            <div class="cell available" *ngIf="showQuantityColumn">
                <input
                    class="table-input"
                    [value]="getGroupTotals(group).quantity"
                    disabled
                />
            </div>
            <div class="cell shipping" *ngIf="showShippingColumn">-</div>
            <div class="cell actions" *ngIf="showRowActionsColumn"></div>
        </div>

        <div
            class="variants-item-row"
            *ngFor="let row of group.rows"
            [class.hidden]="groupedVariantsList.length > 1 && isGroupCollapsed(group.key)"
            [class.grouped-item]="groupedVariantsList.length > 1"
        >
            <div class="cell checkbox" *ngIf="showSelectionColumn">
                <input
                    type="checkbox"
                    [checked]="isVariantSelected(row)"
                    (change)="onVariantSelectChange(row, $event)"
                    [disabled]="isViewOnly"
                />
            </div>
            <div class="cell variant-title">
                <div class="variant-label">
                    <div class="variant-text">
                        <div class="variant-name">
                            {{
                                groupedVariantsList.length > 1
                                    ? getGroupedRowLabel(row)
                                    : getVariantLabel(row)
                            }}
                        </div>
                    </div>
                </div>
            </div>
            <div class="cell image" *ngIf="showImageColumn">
                <div class="variant-image-cell" [class.readonly]="isViewOnly">
                    <ng-container *ngIf="row.image; else variantImagePlaceholder">
                        <div class="variant-image-thumb">
                            <p-image
                                [src]="row.image"
                                [preview]="true"
                                appendTo="body"
                                styleClass="variant-image-preview-wrapper"
                                imageStyleClass="variant-image-preview"
                            ></p-image>
                            <button
                                *ngIf="!isViewOnly"
                                type="button"
                                class="variant-image-edit"
                                (click)="variantImageInput.click()"
                                [disabled]="isViewOnly"
                            >
                                <i class="pi pi-pencil"></i>
                            </button>
                        </div>
                        <button
                            *ngIf="!isViewOnly"
                            type="button"
                            class="variant-image-remove"
                            (click)="clearVariantImage(row)"
                            [disabled]="isViewOnly"
                        >
                            <i class="pi pi-times"></i>
                        </button>
                    </ng-container>
                    <ng-template #variantImagePlaceholder>
                        <ng-container *ngIf="!isViewOnly; else viewOnlyImagePlaceholder">
                            <button
                                type="button"
                                class="variant-image-upload"
                                (click)="variantImageInput.click()"
                                [disabled]="isViewOnly"
                            >
                                <i class="pi pi-image"></i>
                            </button>
                        </ng-container>
                        <ng-template #viewOnlyImagePlaceholder>
                            <div class="variant-image-upload" aria-hidden="true">
                                <i class="pi pi-image"></i>
                            </div>
                        </ng-template>
                    </ng-template>
                    <input
                        type="file"
                        accept="image/*"
                        hidden
                        #variantImageInput
                        (change)="onVariantImageSelect(row, $event)"
                    />
                </div>
            </div>
            <div class="cell price" *ngIf="showPriceColumn">
                <div class="price-wrapper variant-price">
                    <span class="price-prefix">{{ "" | currency | currencySymbol }}</span>
                    <p-inputNumber
                        [style]="{ width: '100%' }"
                        inputStyleClass="table-input variant-price-input"
                        [ngModel]="row.price"
                        (ngModelChange)="onVariantPriceChange(row, $event)"
                        (onBlur)="onVariantPriceBlur(row)"
                        (onFocus)="onVariantPriceFocus(row, $event)"
                        [disabled]="isViewOnly"
                        mode="decimal"
                        [format]="true"
                        [useGrouping]="true"
                        [min]="0"
                        [max]="100000"
                        [minFractionDigits]="2"
                        [maxFractionDigits]="2"
                        placeholder="0.00"
                    ></p-inputNumber>
                </div>
            </div>
            <div class="cell sku" *ngIf="showSkuColumn">
                <div class="variant-code-field">
                    <input
                        class="table-input"
                        type="text"
                        [ngModel]="row.sku"
                        (ngModelChange)="onVariantSkuChange(row, $event)"
                        [disabled]="isViewOnly || useParentSkuBarcodeForVariants"
                        [class.is-invalid]="isVariantSkuInvalid(row.id)"
                    />
                    <div class="variant-code-error" *ngIf="getVariantSkuError(row.id) as skuError">
                        {{ skuError }}
                    </div>
                </div>
            </div>
            <div class="cell barcode" *ngIf="showBarcodeColumn">
                <div class="variant-code-field">
                    <input
                        class="table-input"
                        type="text"
                        [ngModel]="row.barcode"
                        (ngModelChange)="onVariantBarcodeChange(row, $event)"
                        [disabled]="isViewOnly || useParentSkuBarcodeForVariants"
                        [class.is-invalid]="isVariantBarcodeInvalid(row.id)"
                    />
                    <div class="variant-code-error" *ngIf="getVariantBarcodeError(row.id) as barcodeError">
                        {{ barcodeError }}
                    </div>
                </div>
            </div>
            <div class="cell available" *ngIf="showQuantityColumn">
                <input
                    class="table-input"
                    type="number"
                    min="0"
                    step="1"
                    [ngModel]="row.available"
                    (ngModelChange)="onVariantQuantityChange(row, $event)"
                    (keydown)="onQuantityKeydown($event)"
                    [disabled]="isViewOnly"
                />
            </div>
            <div class="cell shipping" *ngIf="showShippingColumn">
                <button
                    type="button"
                    class="icon-button variant-shipping-trigger"
                    [class.variant-shipping-trigger--mismatch]="isShippingUnitMismatched(row)"
                    (click)="openShippingPanel(row)"
                    [disabled]="isViewOnly"
                    [title]="isShippingUnitMismatched(row) ? (getShippingSummary(row) + ' — different unit than most other variants') : getShippingSummary(row)"
                >
                    <i class="pi pi-truck"></i>
                    <span>{{ getShippingSummary(row) }}</span>
                    <i
                        class="pi pi-exclamation-triangle variant-shipping-mismatch-icon"
                        *ngIf="isShippingUnitMismatched(row)"
                    ></i>
                </button>
            </div>
            <div class="cell actions" *ngIf="showRowActionsColumn">
                <button
                    type="button"
                    class="icon-button danger"
                    (click)="deleteVariant(row)"
                    [disabled]="isViewOnly"
                >
                    <i class="pi pi-trash"></i>
                </button>
            </div>
        </div>
        </ng-container>
    </div>
</div>

<p-dialog
    header="Shipping for this variant"
    [(visible)]="isShippingDialogVisible"
    [modal]="true"
    [draggable]="false"
    [resizable]="false"
    [dismissableMask]="true"
    [style]="{ width: '480px' }"
    [breakpoints]="{ '640px': '94vw' }"
    styleClass="variant-shipping-dialog"
    (onHide)="onShippingPanelHide()"
>
    <div class="variant-shipping-panel" *ngIf="activeShippingDraft as draft">
        <div class="variant-shipping-fields">
            <div class="field-checkbox variant-shipping-apply-all">
                <p-checkbox
                    [binary]="true"
                    inputId="applyShippingToAllVariants"
                    [ngModel]="applyShippingToAllVariants"
                    (ngModelChange)="onApplyShippingToAllChange($event)"
                    [disabled]="isViewOnly"
                ></p-checkbox>
                <label for="applyShippingToAllVariants">Same shipping for all variants</label>
            </div>
            <div class="variant-shipping-grid">
                <div class="variant-shipping-field">
                    <label class="control-label">Weight</label>
                    <p-inputNumber
                        styleClass="variant-shipping-input"
                        [inputStyle]="{ width: '100%' }"
                        [ngModel]="draft.shippingWeight"
                        (ngModelChange)="onDraftShippingFieldChange('shippingWeight', $event)"
                        (onFocus)="onDraftShippingFieldFocus('shippingWeight', $event)"
                        (onBlur)="onDraftShippingFieldBlur('shippingWeight')"
                        mode="decimal"
                        [format]="true"
                        [useGrouping]="true"
                        [allowEmpty]="true"
                        [max]="100000"
                        [minFractionDigits]="2"
                        [maxFractionDigits]="2"
                        [disabled]="isViewOnly"
                    ></p-inputNumber>
                </div>
                <div class="variant-shipping-field">
                    <label class="control-label">Dimensions</label>
                    <p-dropdown
                        [style]="{ width: '100%' }"
                        appendTo="body"
                        [options]="_abd.DimensionUOM"
                        [ngModel]="getDraftDimensionUomOption()"
                        (ngModelChange)="onDraftDimensionUomChange($event)"
                        optionLabel="title"
                        [filter]="false"
                        [disabled]="isViewOnly"
                    ></p-dropdown>
                </div>
                <div class="variant-shipping-field">
                    <label class="control-label">Height</label>
                    <p-inputNumber
                        styleClass="variant-shipping-input"
                        [inputStyle]="{ width: '100%' }"
                        [ngModel]="draft.shippingHeight"
                        (ngModelChange)="onDraftShippingFieldChange('shippingHeight', $event)"
                        (onFocus)="onDraftShippingFieldFocus('shippingHeight', $event)"
                        (onBlur)="onDraftShippingFieldBlur('shippingHeight')"
                        mode="decimal"
                        [format]="true"
                        [useGrouping]="true"
                        [allowEmpty]="true"
                        [max]="100000"
                        [minFractionDigits]="2"
                        [maxFractionDigits]="2"
                        [disabled]="isViewOnly"
                    ></p-inputNumber>
                </div>
                <div class="variant-shipping-field">
                    <label class="control-label">Width</label>
                    <p-inputNumber
                        styleClass="variant-shipping-input"
                        [inputStyle]="{ width: '100%' }"
                        [ngModel]="draft.shippingWidth"
                        (ngModelChange)="onDraftShippingFieldChange('shippingWidth', $event)"
                        (onFocus)="onDraftShippingFieldFocus('shippingWidth', $event)"
                        (onBlur)="onDraftShippingFieldBlur('shippingWidth')"
                        mode="decimal"
                        [format]="true"
                        [useGrouping]="true"
                        [allowEmpty]="true"
                        [max]="100000"
                        [minFractionDigits]="2"
                        [maxFractionDigits]="2"
                        [disabled]="isViewOnly"
                    ></p-inputNumber>
                </div>
                <div class="variant-shipping-field">
                    <label class="control-label">Depth</label>
                    <p-inputNumber
                        styleClass="variant-shipping-input"
                        [inputStyle]="{ width: '100%' }"
                        [ngModel]="draft.shippingLength"
                        (ngModelChange)="onDraftShippingFieldChange('shippingLength', $event)"
                        (onFocus)="onDraftShippingFieldFocus('shippingLength', $event)"
                        (onBlur)="onDraftShippingFieldBlur('shippingLength')"
                        mode="decimal"
                        [format]="true"
                        [useGrouping]="true"
                        [allowEmpty]="true"
                        [max]="100000"
                        [minFractionDigits]="2"
                        [maxFractionDigits]="2"
                        [disabled]="isViewOnly"
                    ></p-inputNumber>
                </div>
            </div>
        </div>
    </div>
    <ng-template pTemplate="footer">
        <div class="variant-shipping-footer">
            <button
                type="button"
                pButton
                label="Reset to zero"
                icon="pi pi-refresh"
                class="p-button-text p-button-danger"
                [disabled]="isViewOnly"
                (click)="resetShippingDraft()"
            ></button>
            <div class="variant-shipping-footer-actions">
                <button
                    type="button"
                    pButton
                    label="Cancel"
                    class="p-button-text"
                    (click)="cancelShippingDialog()"
                ></button>
                <button
                    type="button"
                    pButton
                    label="Save"
                    [disabled]="isViewOnly"
                    (click)="saveShippingDialog()"
                ></button>
            </div>
        </div>
    </ng-template>
</p-dialog>


